// src/components/CouponSlider.js
import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { Tag, Copy, Check, ChevronLeft, ChevronRight, Percent, Calendar, Zap } from 'lucide-react';

const CouponSlider = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        const q = query(collection(db, 'coupons'), where('active', '==', true));
        const snap = await getDocs(q);
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));

        // Filter: not expired, not limit reached
        const now = new Date();
        const valid = data.filter(c => {
          if (c.expiryDate) {
            const expiry = new Date(c.expiryDate);
            if (expiry < now) return false;
          }
          if (c.usageLimit && (c.usedCount || 0) >= c.usageLimit) return false;
          return true;
        });

        setCoupons(valid);
      } catch (error) {
        console.error('Error fetching coupons:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCoupons();
  }, []);

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(''), 2000);
  };

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const amount = 340;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -amount : amount,
      behavior: 'smooth',
    });
  };

  const formatExpiry = (date) => {
    if (!date) return '';
    try {
      const d = new Date(date);
      return d.toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return ''; }
  };

  // Generate random gradient colors for variety
  const gradients = [
    'linear-gradient(135deg, #16a34a 0%, #14532d 100%)',
    'linear-gradient(135deg, #f59e0b 0%, #b45309 100%)',
    'linear-gradient(135deg, #ec4899 0%, #831843 100%)',
    'linear-gradient(135deg, #3b82f6 0%, #1e3a8a 100%)',
    'linear-gradient(135deg, #8b5cf6 0%, #5b21b6 100%)',
    'linear-gradient(135deg, #ef4444 0%, #991b1b 100%)',
  ];

  if (loading || coupons.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12 anim-fadeInUp">

      {/* Header */}
      <div className="text-center mb-6 md:mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3"
          style={{ backgroundColor: 'rgba(22, 163, 74, 0.1)', color: 'var(--color-link)' }}>
          <Tag size={14} />
          <span className="text-xs font-bold uppercase tracking-wider">Active Coupons</span>
        </div>
        <h2 className="text-2xl md:text-4xl font-bold mb-2" style={{ color: 'var(--color-primary-text)' }}>
          Grab These Deals!
        </h2>
        <p className="text-sm md:text-base" style={{ color: 'var(--color-secondary-text)' }}>
          Copy karein aur checkout par use karein
        </p>
      </div>

      {/* Slider */}
      <div className="relative">

        {/* Left Arrow */}
        {coupons.length > 2 && (
          <button
            onClick={() => scroll('left')}
            className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full shadow-lg btn-2d"
            style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)`, color: 'var(--color-primary-text)' }}
          >
            <ChevronLeft size={20} />
          </button>
        )}

        {/* Scroll Container */}
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto scroll-smooth pb-4 snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {coupons.map((coupon, i) => {
            const gradient = gradients[i % gradients.length];
            const isCopied = copiedCode === coupon.code;

            return (
              <div
                key={coupon.id}
                className="relative shrink-0 w-[280px] md:w-[320px] rounded-2xl overflow-hidden shadow-lg group snap-start anim-scaleIn"
                style={{
                  background: gradient,
                  animationDelay: `${i * 80}ms`,
                }}
              >
                {/* Decorative Circles */}
                <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-white opacity-5 -translate-y-1/2 translate-x-1/2"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full bg-white opacity-5 translate-y-1/2 -translate-x-1/2"></div>

                {/* Content */}
                <div className="relative p-5 text-white">

                  {/* Badge */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/20 backdrop-blur-md">
                      <Zap size={12} className="fill-current" />
                      <span className="text-[10px] font-bold uppercase tracking-wider">Limited Time</span>
                    </div>
                    <Percent size={20} className="opacity-60" />
                  </div>

                  {/* Discount Value */}
                  <div className="mb-3">
                    <p className="text-4xl md:text-5xl font-bold">
                      {coupon.type === 'percentage' ? `${coupon.value}%` : `Rs. ${coupon.value}`}
                    </p>
                    <p className="text-xs opacity-90 mt-1">
                      {coupon.type === 'percentage' ? 'PERCENTAGE OFF' : 'FLAT DISCOUNT'}
                    </p>
                  </div>

                  {/* Description */}
                  {coupon.description && (
                    <p className="text-xs opacity-90 mb-3 italic line-clamp-2">
                      "{coupon.description}"
                    </p>
                  )}

                  {/* Details */}
                  <div className="space-y-1 text-xs opacity-90 mb-4">
                    {coupon.minOrder > 0 && (
                      <p>Min order: Rs. {coupon.minOrder}</p>
                    )}
                    {coupon.maxDiscount > 0 && (
                      <p>Max discount: Rs. {coupon.maxDiscount}</p>
                    )}
                    {coupon.expiryDate && (
                      <p className="flex items-center gap-1">
                        <Calendar size={10} /> Valid till: {formatExpiry(coupon.expiryDate)}
                      </p>
                    )}
                  </div>

                  {/* Coupon Code Box */}
                  <div
                    className="relative flex items-center justify-between rounded-xl p-3 backdrop-blur-md"
                    style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: '1px dashed rgba(255,255,255,0.5)' }}
                  >
                    <div>
                      <p className="text-[10px] uppercase opacity-80">Coupon Code</p>
                      <p className="text-lg font-bold font-mono tracking-wider">{coupon.code}</p>
                    </div>

                    <button
                      onClick={() => copyCode(coupon.code)}
                      className="flex items-center gap-1 px-3 py-2 rounded-lg bg-white text-xs font-bold transition-all btn-2d"
                      style={{ color: '#16a34a' }}
                    >
                      {isCopied ? (
                        <>
                          <Check size={14} /> Copied!
                        </>
                      ) : (
                        <>
                          <Copy size={14} /> Copy
                        </>
                      )}
                    </button>
                  </div>

                </div>
              </div>
            );
          })}
        </div>

        {/* Right Arrow */}
        {coupons.length > 2 && (
          <button
            onClick={() => scroll('right')}
            className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full shadow-lg btn-2d"
            style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)`, color: 'var(--color-primary-text)' }}
          >
            <ChevronRight size={20} />
          </button>
        )}
      </div>

      {/* Swipe Hint */}
      {coupons.length > 2 && (
        <p className="text-center text-xs mt-3 md:hidden" style={{ color: 'var(--color-secondary-text)' }}>
          ← Swipe to see more →
        </p>
      )}
    </section>
  );
};

export default CouponSlider;